import { Controller } from "react-hook-form";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/inputs/TextInput";
import { Button } from "../../../components/button";
import { useCallback, useEffect, useState } from "react";
import {
  useGetAllLocations,
  useGetLocationById,
} from "../../../hooks/common/query";
import toast, { Toaster } from "react-hot-toast";
import LocationsTable from "../../../components/table/LocationsTable";
import { LocationsSchema } from "../../../schemas/locationsSchema";
import {
  useAddUpdateLocation,
  useIsLocationExists,
} from "../../../hooks/common/mutation";
import useAuthStore from "../../../store/Auth";
import { PAGE_SIZE } from "../../../constants/common";
import BackButtonWrapper from "../../../components/button/BackButton/wrapper";

interface LocationData {
  locationEn: string;
  locationAr: string;
  description?: string | null;
}

const Locations = () => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    handleSubmit,
    resetField,
    setValue,
  } = useFormWithSchema(LocationsSchema);

  const [allLocations, setAllLocations] = useState([]);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [selectedLocationId, setSelectedLocationId] = useState(0);

  const { mutateAsync: IsLocationExists } = useIsLocationExists();
  const { data: locations } = useGetAllLocations({
    pageNumber: page,
    pageSize: pageSize,
    search: search,
  });

  useEffect(() => {
    if (locations) {
      setAllLocations(locations.data || []);
      setPages(locations.pagination?.pages || 1);
      setCount(locations.pagination?.count || 0);
    }
  }, [setPages, locations]);

  const {
    mutateAsync: AddUpdateLocation,
    isPending: isAddUpdateLocationLoading,
  } = useAddUpdateLocation();

  const user = useAuthStore((state) => state.user);

  const { data: selectedLocation } = useGetLocationById({
    encryptedId: selectedLocationId ? selectedLocationId.toString() : "",
  });

  useEffect(() => {
    if (selectedLocation) {
      setValue("locationEn", selectedLocation.s_LocationName_En);
      setValue("locationAr", selectedLocation.s_LocationName_Ar);
      setValue("description", selectedLocation.s_Description);
    }
  }, [setValue, selectedLocation]);

  const onSubmit = useCallback(
    async (data: LocationData) => {
      if (selectedLocationId === 0) {
        try {
          const isExistBody = {
            n_LocationID: selectedLocationId,
            s_LocationName_Ar: data.locationAr,
            s_LocationName_En: data.locationEn,
          };
          const { isExists } = await IsLocationExists(isExistBody);

          if (isExists) {
            toast.error(t("locationExists"));
            return;
          }
        } catch (error) {
          toast.error(t("somethingWentWrong"));
          console.error("Error checking if location exists", error);
          return;
        }
      }

      try {
        if (user?.user_id) {
          const body = {
            n_LocationID: 0,
            s_LocationName_Ar: data.locationAr,
            s_LocationName_En: data.locationEn,
            s_Description: data.description,
          };

          if (selectedLocationId === 0) {
            await AddUpdateLocation(body);
          } else {
            const updateBody = {
              ...body,
              n_LocationID: selectedLocationId,
            };

            await AddUpdateLocation(updateBody);
          }
        }

        setSelectedLocationId(0);

        if (selectedLocationId === 0) {
          toast.success(t("locationAdded"));
        } else {
          toast.success(t("locationUpdated"));
        }

        resetField("locationEn");
        resetField("locationAr");
        resetField("description");
      } catch (error) {
        toast.error(t("somethingWentWrong"));
        console.error("Error adding/updating location", error);
      }
    },
    [
      AddUpdateLocation,
      IsLocationExists,
      resetField,
      selectedLocationId,
      user?.user_id,
      t,
    ]
  );

  const onClear = useCallback(() => {
    setSelectedLocationId(0);

    resetField("description");
    resetField("locationAr");
    resetField("locationEn");
  }, [resetField]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("locations")}
          </h1>
        </BackButtonWrapper>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 md:gap-8"
        >
          <div className="bg-white p-4 rounded-xl grid sm:grid-cols-3 gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
            <Controller
              name="locationEn"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("locationNameEn")}
                  onChange={onChange}
                  value={value || ""}
                  error={errors?.[name]?.message}
                  required
                />
              )}
            />

            <Controller
              name="locationAr"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("locationNameAr")}
                  onChange={onChange}
                  value={value || ""}
                  required
                  error={errors?.[name]?.message}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field: { onChange, name, value } }) => {
                return (
                  <TextInput
                    label={t("description")}
                    onChange={onChange}
                    value={value || ""}
                    error={errors?.[name]?.message}
                  />
                );
              }}
            />

            <div className="flex items-center gap-2">
              <Button isLoading={isAddUpdateLocationLoading}>
                {t("save")}
              </Button>
              <Button
                type="button"
                onClick={onClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </div>

          <LocationsTable
            locations={allLocations}
            setSelectedLocationId={setSelectedLocationId}
            page={page}
            pages={pages}
            setPage={setPage}
            search={search}
            setSearch={setSearch}
            count={count}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </form>
      </div>
    </>
  );
};

export default Locations;
